import React, { useState, useEffect } from 'react';
import { SAPLayout } from '@/layouts/SAPLayout/SAPLayout';
import { mockSapSimulatorService } from '@/services/mock/MockSapSimulatorService';
import { SapPoRecord } from '@/types/domain/sap-simulator';
import { DataTable, ColumnDef } from '@/components/data-display/DataTable';
import { formatCurrency } from '@/lib/utils';
import { ShoppingBag } from 'lucide-react';

export const SapPoPage: React.FC = () => {
  const [pos, setPos] = useState<SapPoRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadPos = async () => {
      try {
        setIsLoading(true);
        const data = await mockSapSimulatorService.getPos();
        setPos(data.data);
      } finally {
        setIsLoading(false);
      }
    };
    loadPos();
  }, []);

  const columns: ColumnDef<SapPoRecord>[] = [
    {
      header: 'SAP PO Number',
      accessorKey: 'poNumber',
      cell: (row) => <span className="font-mono font-extrabold text-emerald-600 text-xs">{row.poNumber}</span>,
    },
    {
      header: 'PR Reference',
      accessorKey: 'sapPrNumber',
      cell: (row) => <span className="font-mono font-bold text-primary text-xs">{row.sapPrNumber}</span>,
    },
    {
      header: 'Vendor',
      cell: (row) => (
        <div className="text-xs">
          <span className="font-bold text-foreground">{row.vendorName}</span>
          <p className="text-[11px] font-mono text-foreground-muted">{row.vendorCode}</p>
        </div>
      ),
    },
    {
      header: 'PO Date & Delivery',
      cell: (row) => (
        <span className="text-xs text-foreground-secondary font-medium">
          {row.poDate} → Delivery {row.deliveryDate}
        </span>
      ),
    },
    {
      header: 'Qty (Delivered / Pending)',
      cell: (row) => (
        <span className="text-xs font-semibold text-foreground">
          {row.quantity} ({row.deliveredQuantity} / {row.pendingQuantity})
        </span>
      ),
    },
    {
      header: 'Total Value',
      accessorKey: 'totalValue',
      cell: (row) => <span className="font-extrabold text-xs text-emerald-600">{formatCurrency(row.totalValue, row.currency)}</span>,
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: (row) => (
        <span className="text-[10px] font-extrabold uppercase bg-emerald-500/10 text-emerald-700 px-2 py-0.5 rounded-full">
          {row.status}
        </span>
      ),
    },
  ];

  return (
    <SAPLayout>
      <div className="space-y-6 font-heading">
        <div>
          <h2 className="text-xl font-extrabold text-foreground flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-emerald-600" /> SAP Purchase Orders (ME21N)
          </h2>
          <p className="text-xs text-foreground-secondary font-medium">
            Simulated ME21N PO records linked directly to SAP Purchase Requisitions and Vendor Masters.
          </p>
        </div>

        <DataTable data={pos} columns={columns} isLoading={isLoading} />
      </div>
    </SAPLayout>
  );
};
